import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reviewService } from '../../services/reviewService';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/helpers';
import StarRating from './StarRating';
import ReviewForm from './ReviewForm';
import './ProductReviews.css';

const EMPTY_SUMMARY = { average: null, count: 0, distribution: {} };

const ProductReviews = ({ productSlug, productId, onSummaryChange }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Held in a ref so an inline callback from the parent cannot re-trigger the
  // load effect on every render.
  const onSummaryChangeRef = useRef(onSummaryChange);
  onSummaryChangeRef.current = onSummaryChange;

  const loadReviews = useCallback(async () => {
    try {
      const data = await reviewService.getProductReviews(productSlug);
      const nextSummary = data?.summary || EMPTY_SUMMARY;
      setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
      setSummary(nextSummary);
      if (onSummaryChangeRef.current) onSummaryChangeRef.current(nextSummary);
    } catch (err) {
      setError(err.message || 'Failed to load reviews');
    }
  }, [productSlug]);

  const loadEligibility = useCallback(async () => {
    if (!isAuthenticated || !productId) {
      setEligibility(null);
      return;
    }
    try {
      setEligibility(await reviewService.getEligibility(productId));
    } catch (err) {
      // A failed eligibility probe just means we hide the form, never block reviews.
      setEligibility(null);
    }
  }, [isAuthenticated, productId]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      await Promise.all([loadReviews(), loadEligibility()]);
      if (active) setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [loadReviews, loadEligibility]);

  const handleSaved = async () => {
    setIsEditing(false);
    await Promise.all([loadReviews(), loadEligibility()]);
  };

  const handleDelete = async reviewId => {
    if (!window.confirm('Delete your review?')) return;
    try {
      await reviewService.deleteReview(reviewId);
      await handleSaved();
    } catch (err) {
      setError(err.message || 'Failed to delete review');
    }
  };

  const ownReview = eligibility?.review || null;
  const canWrite = Boolean(eligibility?.canReview);
  const showForm = canWrite || isEditing;

  const renderPrompt = () => {
    if (showForm || ownReview) return null;
    if (!isAuthenticated) {
      return (
        <p className="reviews-prompt">
          <button
            type="button"
            className="reviews-prompt-link"
            onClick={() => navigate('/login')}
          >
            Sign in
          </button>{' '}
          to review this product after your order is delivered.
        </p>
      );
    }
    return (
      <p className="reviews-prompt">
        Only customers whose order for this product has been delivered can leave
        a review.
      </p>
    );
  };

  return (
    <div className="product-reviews">
      <div className="reviews-header">
        <h3>Ratings &amp; Reviews</h3>
        {summary.count > 0 && (
          <div className="reviews-summary">
            <span className="reviews-summary-average">{summary.average}</span>
            <div className="reviews-summary-meta">
              <StarRating value={summary.average} size="md" />
              <span className="reviews-summary-count">
                {summary.count} {summary.count === 1 ? 'review' : 'reviews'}
              </span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="reviews-error">{error}</div>}

      {renderPrompt()}

      {showForm && (
        <ReviewForm
          productId={productId}
          existingReview={isEditing ? ownReview : null}
          onSaved={handleSaved}
          onCancel={isEditing ? () => setIsEditing(false) : null}
        />
      )}

      {ownReview && !isEditing && (
        <div className="reviews-own-actions">
          <span>You reviewed this product.</span>
          <button type="button" onClick={() => setIsEditing(true)}>
            Edit
          </button>
          <button type="button" onClick={() => handleDelete(ownReview.id)}>
            Delete
          </button>
        </div>
      )}

      {loading ? (
        <p className="reviews-loading">Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <p className="reviews-empty">No reviews yet.</p>
      ) : (
        <ul className="reviews-list">
          {reviews.map(review => (
            <li key={review.id} className="review-item">
              <div className="review-item-head">
                <StarRating value={review.rating} size="sm" />
                <span className="review-item-author">
                  {review.user?.full_name || 'Verified buyer'}
                </span>
                <span className="review-item-verified">Verified purchase</span>
                <span className="review-item-date">
                  {formatDate(review.created_at)}
                </span>
              </div>
              {review.title && (
                <p className="review-item-title">{review.title}</p>
              )}
              {review.comment && (
                <p className="review-item-comment">{review.comment}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductReviews;
